export type ImageMetadataFormat = 'jpeg' | 'png' | 'webp'

export interface MetadataSummary {
  exif: boolean
  xmp: boolean
  text: boolean
  time: boolean
  colorProfilePreserved: boolean
}

export interface InspectedMetadata {
  format: ImageMetadataFormat
  summary: MetadataSummary
  width: number
  height: number
  frameCount: number
}

export interface CleanedMetadata extends InspectedMetadata {
  bytes: Uint8Array
}

export type MetadataProcessingErrorCode =
  | 'unsupported_type'
  | 'corrupt_image'
  | 'too_large_dimensions'

export class MetadataProcessingError extends Error {
  constructor(readonly code: MetadataProcessingErrorCode) {
    super(code)
    this.name = 'MetadataProcessingError'
  }
}

const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10])
const PNG_REMOVED_CHUNKS = new Set(['eXIf', 'tEXt', 'zTXt', 'iTXt', 'tIME'])
const WEBP_REMOVED_CHUNKS = new Set(['EXIF', 'XMP '])
const ASCII_DECODER = new TextDecoder('latin1')
const MAX_CHUNKS = 10_000
export const MAX_IMAGE_PIXELS = 36_000_000
export const MAX_IMAGE_SIDE = 32_768
export const MAX_ANIMATION_FRAMES = 100
export const MAX_TOTAL_FRAME_PIXELS = 100_000_000

interface ParsedChunk {
  type: string
  start: number
  end: number
  dataStart: number
  dataEnd: number
}

interface ChunkBudget {
  remaining: number
}

function emptySummary(): MetadataSummary {
  return {
    exif: false,
    xmp: false,
    text: false,
    time: false,
    colorProfilePreserved: false,
  }
}

function failCorrupt(): never {
  throw new MetadataProcessingError('corrupt_image')
}

function consumeChunk(budget: ChunkBudget): void {
  if (budget.remaining <= 0) failCorrupt()
  budget.remaining -= 1
}

function validateDimensions(width: number, height: number): void {
  if (
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width <= 0 ||
    height <= 0 ||
    width > MAX_IMAGE_SIDE ||
    height > MAX_IMAGE_SIDE ||
    width > Math.floor(MAX_IMAGE_PIXELS / height)
  ) {
    throw new MetadataProcessingError('too_large_dimensions')
  }
}

function validateAnimationBounds(width: number, height: number, frameCount: number): void {
  const pixelsPerFrame = width * height
  if (
    !Number.isInteger(frameCount) ||
    frameCount <= 0 ||
    frameCount > MAX_ANIMATION_FRAMES ||
    frameCount > Math.floor(MAX_TOTAL_FRAME_PIXELS / pixelsPerFrame)
  ) {
    throw new MetadataProcessingError('too_large_dimensions')
  }
}

function hasPrefix(bytes: Uint8Array, prefix: Uint8Array): boolean {
  return prefix.every((value, index) => bytes[index] === value)
}

function readUint32BigEndian(bytes: Uint8Array, offset: number): number {
  if (offset < 0 || offset + 4 > bytes.length) failCorrupt()
  return (
    bytes[offset]! * 0x1000000 +
    (bytes[offset + 1]! << 16) +
    (bytes[offset + 2]! << 8) +
    bytes[offset + 3]!
  )
}

function readUint32LittleEndian(bytes: Uint8Array, offset: number): number {
  if (offset < 0 || offset + 4 > bytes.length) failCorrupt()
  return (
    bytes[offset]! +
    bytes[offset + 1]! * 0x100 +
    bytes[offset + 2]! * 0x10000 +
    bytes[offset + 3]! * 0x1000000
  )
}

function writeUint32LittleEndian(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = value & 0xff
  bytes[offset + 1] = (value >>> 8) & 0xff
  bytes[offset + 2] = (value >>> 16) & 0xff
  bytes[offset + 3] = (value >>> 24) & 0xff
}

function ascii(bytes: Uint8Array, start: number, end: number): string {
  return ASCII_DECODER.decode(bytes.subarray(start, end))
}

let crcTable: Uint32Array | undefined

function getCrcTable(): Uint32Array {
  if (crcTable) return crcTable
  crcTable = new Uint32Array(256)
  for (let index = 0; index < 256; index += 1) {
    let value = index
    for (let bit = 0; bit < 8; bit += 1) {
      value = (value & 1) !== 0 ? 0xedb88320 ^ (value >>> 1) : value >>> 1
    }
    crcTable[index] = value >>> 0
  }
  return crcTable
}

function crc32(bytes: Uint8Array, start: number, end: number): number {
  const table = getCrcTable()
  let crc = 0xffffffff
  for (let offset = start; offset < end; offset += 1) {
    crc = table[(crc ^ bytes[offset]!) & 0xff]! ^ (crc >>> 8)
  }
  return (crc ^ 0xffffffff) >>> 0
}

function parsePng(bytes: Uint8Array): ParsedChunk[] {
  if (bytes.length < PNG_SIGNATURE.length || !hasPrefix(bytes, PNG_SIGNATURE)) {
    failCorrupt()
  }

  const chunks: ParsedChunk[] = []
  let offset = PNG_SIGNATURE.length
  let sawIend = false
  while (offset < bytes.length) {
    if (chunks.length >= MAX_CHUNKS) failCorrupt()
    if (sawIend || offset + 12 > bytes.length) failCorrupt()
    const length = readUint32BigEndian(bytes, offset)
    const dataStart = offset + 8
    const dataEnd = dataStart + length
    const end = dataEnd + 4
    if (!Number.isSafeInteger(dataEnd) || dataEnd < dataStart || end > bytes.length) {
      failCorrupt()
    }
    const type = ascii(bytes, offset + 4, offset + 8)
    if (!/^[A-Za-z]{4}$/.test(type)) failCorrupt()
    const storedCrc = readUint32BigEndian(bytes, dataEnd)
    if (crc32(bytes, offset + 4, dataEnd) !== storedCrc) failCorrupt()
    if (chunks.length === 0 && (type !== 'IHDR' || length !== 13)) failCorrupt()
    if (type === 'IEND') {
      if (length !== 0) failCorrupt()
      sawIend = true
    }
    chunks.push({ type, start: offset, end, dataStart, dataEnd })
    offset = end
  }
  if (
    !sawIend ||
    offset !== bytes.length ||
    chunks.filter((chunk) => chunk.type === 'IHDR').length !== 1 ||
    chunks.filter((chunk) => chunk.type === 'acTL').length > 1
  ) {
    failCorrupt()
  }
  return chunks
}

function inspectPng(bytes: Uint8Array, chunks: ParsedChunk[]): MetadataSummary {
  const summary = emptySummary()
  for (const chunk of chunks) {
    if (chunk.type === 'eXIf') summary.exif = true
    if (chunk.type === 'tEXt' || chunk.type === 'zTXt' || chunk.type === 'iTXt') {
      summary.text = true
    }
    if (chunk.type === 'iTXt') {
      const nullOffset = bytes.subarray(chunk.dataStart, chunk.dataEnd).indexOf(0)
      if (
        nullOffset >= 0 &&
        ascii(bytes, chunk.dataStart, chunk.dataStart + nullOffset) ===
          'XML:com.adobe.xmp'
      ) {
        summary.xmp = true
      }
    }
    if (chunk.type === 'tIME') summary.time = true
    if (chunk.type === 'iCCP') summary.colorProfilePreserved = true
  }
  return summary
}

function parseWebp(bytes: Uint8Array): ParsedChunk[] {
  if (
    bytes.length < 20 ||
    ascii(bytes, 0, 4) !== 'RIFF' ||
    ascii(bytes, 8, 12) !== 'WEBP'
  ) {
    failCorrupt()
  }
  const riffSize = readUint32LittleEndian(bytes, 4)
  if (riffSize !== bytes.length - 8) failCorrupt()

  const chunks: ParsedChunk[] = []
  let offset = 12
  while (offset < bytes.length) {
    if (chunks.length >= MAX_CHUNKS) failCorrupt()
    if (offset + 8 > bytes.length) failCorrupt()
    const length = readUint32LittleEndian(bytes, offset + 4)
    const dataStart = offset + 8
    const dataEnd = dataStart + length
    const padding = length % 2
    const end = dataEnd + padding
    if (!Number.isSafeInteger(dataEnd) || dataEnd < dataStart || end > bytes.length) {
      failCorrupt()
    }
    if (padding === 1 && bytes[dataEnd] !== 0) failCorrupt()
    const type = ascii(bytes, offset, offset + 4)
    if (type.length !== 4) failCorrupt()
    if (type === 'VP8X' && length !== 10) failCorrupt()
    chunks.push({ type, start: offset, end, dataStart, dataEnd })
    offset = end
  }
  if (offset !== bytes.length || chunks.length === 0) failCorrupt()
  const extendedHeaders = chunks.filter((chunk) => chunk.type === 'VP8X')
  if (
    extendedHeaders.length > 1 ||
    (extendedHeaders.length === 1 && chunks[0]?.type !== 'VP8X')
  ) {
    failCorrupt()
  }
  if (!chunks.some((chunk) => chunk.type === 'VP8 ' || chunk.type === 'VP8L' || chunk.type === 'VP8X')) {
    failCorrupt()
  }
  return chunks
}

function inspectWebp(chunks: ParsedChunk[]): MetadataSummary {
  const summary = emptySummary()
  for (const chunk of chunks) {
    if (chunk.type === 'EXIF') summary.exif = true
    if (chunk.type === 'XMP ') summary.xmp = true
    if (chunk.type === 'ICCP') summary.colorProfilePreserved = true
  }
  return summary
}

function readWebpLossyDimensions(bytes: Uint8Array, start: number, end: number): { width: number; height: number } {
  if (
    end - start < 10 ||
    bytes[start + 3] !== 0x9d ||
    bytes[start + 4] !== 0x01 ||
    bytes[start + 5] !== 0x2a
  ) {
    failCorrupt()
  }
  return {
    width: (bytes[start + 6]! + bytes[start + 7]! * 256) & 0x3fff,
    height: (bytes[start + 8]! + bytes[start + 9]! * 256) & 0x3fff,
  }
}

function readWebpLosslessDimensions(bytes: Uint8Array, start: number, end: number): { width: number; height: number } {
  if (end - start < 5 || bytes[start] !== 0x2f) failCorrupt()
  const b1 = bytes[start + 1]!
  const b2 = bytes[start + 2]!
  const b3 = bytes[start + 3]!
  const b4 = bytes[start + 4]!
  return {
    width: 1 + b1 + ((b2 & 0x3f) << 8),
    height: 1 + (b2 >> 6) + (b3 << 2) + ((b4 & 0x0f) << 10),
  }
}

function readWebpFrameDimensions(
  bytes: Uint8Array,
  start: number,
  end: number,
  budget: ChunkBudget,
): { width: number; height: number } {
  const imageChunks: { type: string; dataStart: number; dataEnd: number }[] = []
  let sawImage = false
  let alphaChunks = 0
  let offset = start
  while (offset < end) {
    consumeChunk(budget)
    if (offset + 8 > end) failCorrupt()
    const length = readUint32LittleEndian(bytes, offset + 4)
    const dataStart = offset + 8
    const dataEnd = dataStart + length
    const chunkEnd = dataEnd + (length % 2)
    if (!Number.isSafeInteger(dataEnd) || dataEnd < dataStart || chunkEnd > end) failCorrupt()
    if (length % 2 === 1 && bytes[dataEnd] !== 0) failCorrupt()
    const type = ascii(bytes, offset, offset + 4)
    if (type === 'ALPH') {
      if (sawImage || ++alphaChunks > 1) failCorrupt()
    } else if (type === 'VP8 ' || type === 'VP8L') {
      if (sawImage) failCorrupt()
      sawImage = true
      imageChunks.push({ type, dataStart, dataEnd })
    } else {
      failCorrupt()
    }
    offset = chunkEnd
  }
  if (offset !== end || imageChunks.length !== 1) failCorrupt()
  const image = imageChunks[0]!
  return image.type === 'VP8 '
    ? readWebpLossyDimensions(bytes, image.dataStart, image.dataEnd)
    : readWebpLosslessDimensions(bytes, image.dataStart, image.dataEnd)
}

function validateWebpAnimationFrames(
  bytes: Uint8Array,
  chunks: ParsedChunk[],
  canvasWidth: number,
  canvasHeight: number,
  budget: ChunkBudget,
): void {
  for (const chunk of chunks) {
    if (chunk.type !== 'ANMF') continue
    if (chunk.dataEnd - chunk.dataStart < 24) failCorrupt()
    const start = chunk.dataStart
    const x = (bytes[start]! + bytes[start + 1]! * 256 + bytes[start + 2]! * 65_536) * 2
    const y = (bytes[start + 3]! + bytes[start + 4]! * 256 + bytes[start + 5]! * 65_536) * 2
    const width = 1 + bytes[start + 6]! + bytes[start + 7]! * 256 + bytes[start + 8]! * 65_536
    const height = 1 + bytes[start + 9]! + bytes[start + 10]! * 256 + bytes[start + 11]! * 65_536
    if (
      !Number.isSafeInteger(x) || !Number.isSafeInteger(y) ||
      x + width > canvasWidth || y + height > canvasHeight
    ) failCorrupt()
    validateDimensions(width, height)
    const nested = readWebpFrameDimensions(bytes, start + 16, chunk.dataEnd, budget)
    if (nested.width !== width || nested.height !== height) failCorrupt()
  }
}

function readWebpDimensions(
  bytes: Uint8Array,
  chunks: ParsedChunk[],
): { width: number; height: number; frameCount: number } {
  let width = 0
  let height = 0
  const extended = chunks.find((chunk) => chunk.type === 'VP8X')
  if (extended) {
    const start = extended.dataStart
    width = 1 + bytes[start + 4]! + bytes[start + 5]! * 256 + bytes[start + 6]! * 65_536
    height = 1 + bytes[start + 7]! + bytes[start + 8]! * 256 + bytes[start + 9]! * 65_536
  } else {
    const lossy = chunks.find((chunk) => chunk.type === 'VP8 ')
    const lossless = chunks.find((chunk) => chunk.type === 'VP8L')
    if (lossy) {
      ;({ width, height } = readWebpLossyDimensions(bytes, lossy.dataStart, lossy.dataEnd))
    } else if (lossless) {
      ;({ width, height } = readWebpLosslessDimensions(bytes, lossless.dataStart, lossless.dataEnd))
    }
  }
  validateDimensions(width, height)
  const animationFrames = chunks.filter((chunk) => chunk.type === 'ANMF').length
  const animationHeaders = chunks.filter((chunk) => chunk.type === 'ANIM')
  if (
    animationHeaders.length > 1 ||
    animationHeaders.some((chunk) => chunk.dataEnd - chunk.dataStart !== 6) ||
    chunks.some(
      (chunk) => chunk.type === 'ANMF' && chunk.dataEnd - chunk.dataStart < 16,
    )
  ) {
    failCorrupt()
  }
  const animationHeader = animationHeaders.length === 1
  const animationFlag = extended ? (bytes[extended.dataStart]! & 0x02) !== 0 : false
  const topLevelImageChunks = chunks.filter(
    (chunk) => chunk.type === 'VP8 ' || chunk.type === 'VP8L',
  )
  if (
    (animationFlag && topLevelImageChunks.length !== 0) ||
    (!animationFlag && topLevelImageChunks.length !== 1)
  ) {
    failCorrupt()
  }
  if (animationFlag !== animationHeader || animationFlag !== (animationFrames > 0)) {
    failCorrupt()
  }
  const frameCount = Math.max(1, animationFrames)
  validateAnimationBounds(width, height, frameCount)
  if (animationFlag) {
    validateWebpAnimationFrames(
      bytes,
      chunks,
      width,
      height,
      { remaining: MAX_CHUNKS - chunks.length },
    )
  } else if (extended) {
    const image = topLevelImageChunks[0]!
    const bitstreamDimensions = image.type === 'VP8 '
      ? readWebpLossyDimensions(bytes, image.dataStart, image.dataEnd)
      : readWebpLosslessDimensions(bytes, image.dataStart, image.dataEnd)
    validateDimensions(bitstreamDimensions.width, bitstreamDimensions.height)
    if (bitstreamDimensions.width !== width || bitstreamDimensions.height !== height) {
      failCorrupt()
    }
  }
  return { width, height, frameCount }
}

function validateApng(bytes: Uint8Array, chunks: ParsedChunk[], width: number, height: number): number {
  const animation = chunks.find((chunk) => chunk.type === 'acTL')
  const controls = chunks.filter((chunk) => chunk.type === 'fcTL')
  if (!animation) {
    if (controls.length > 0 || chunks.some((chunk) => chunk.type === 'fdAT')) failCorrupt()
    return 1
  }
  if (animation.dataEnd - animation.dataStart !== 8) failCorrupt()
  const animationIndex = chunks.indexOf(animation)
  if (
    chunks.some(
      (chunk, index) =>
        index < animationIndex && (chunk.type === 'IDAT' || chunk.type === 'fcTL'),
    )
  ) {
    failCorrupt()
  }
  const frameCount = readUint32BigEndian(bytes, animation.dataStart)
  validateAnimationBounds(width, height, frameCount)
  if (controls.length !== frameCount) failCorrupt()

  let expectedSequence = 0
  let currentFrame = -1
  const frameHasData = new Array<boolean>(frameCount).fill(false)
  let sawIdat = false
  let defaultImageIsAnimated = false
  for (const chunk of chunks) {
    if (chunk.type === 'fcTL') {
      if (chunk.dataEnd - chunk.dataStart !== 26 || currentFrame + 1 >= frameCount) failCorrupt()
      if (readUint32BigEndian(bytes, chunk.dataStart) !== expectedSequence++) failCorrupt()
      if (!sawIdat && currentFrame === -1) defaultImageIsAnimated = true
      currentFrame += 1
      const frameWidth = readUint32BigEndian(bytes, chunk.dataStart + 4)
      const frameHeight = readUint32BigEndian(bytes, chunk.dataStart + 8)
      const x = readUint32BigEndian(bytes, chunk.dataStart + 12)
      const y = readUint32BigEndian(bytes, chunk.dataStart + 16)
      validateDimensions(frameWidth, frameHeight)
      if (bytes[chunk.dataStart + 24]! > 2 || bytes[chunk.dataStart + 25]! > 1) failCorrupt()
      if (x > width || y > height || frameWidth > width - x || frameHeight > height - y) failCorrupt()
    } else if (chunk.type === 'IDAT') {
      if (
        (defaultImageIsAnimated && currentFrame !== 0) ||
        (!defaultImageIsAnimated && currentFrame !== -1)
      ) failCorrupt()
      sawIdat = true
      if (defaultImageIsAnimated) frameHasData[0] = true
    } else if (chunk.type === 'fdAT') {
      if (
        chunk.dataEnd - chunk.dataStart <= 4 ||
        currentFrame < 0 ||
        !sawIdat ||
        (defaultImageIsAnimated && currentFrame === 0)
      ) failCorrupt()
      if (readUint32BigEndian(bytes, chunk.dataStart) !== expectedSequence++) failCorrupt()
      frameHasData[currentFrame] = true
    }
  }
  if (!sawIdat || currentFrame !== frameCount - 1 || frameHasData.some((hasData) => !hasData)) failCorrupt()
  return frameCount
}

function concat(parts: Uint8Array[]): Uint8Array {
  const length = parts.reduce((total, part) => total + part.length, 0)
  const result = new Uint8Array(length)
  let offset = 0
  for (const part of parts) {
    result.set(part, offset)
    offset += part.length
  }
  return result
}

function inspectJpeg(bytes: Uint8Array): InspectedMetadata {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) failCorrupt()
  const summary = emptySummary()
  let width = 0
  let height = 0
  let offset = 2
  let segmentCount = 0
  while (offset < bytes.length) {
    segmentCount += 1
    if (segmentCount > MAX_CHUNKS) failCorrupt()
    if (bytes[offset] !== 0xff) failCorrupt()
    while (offset < bytes.length && bytes[offset] === 0xff) offset += 1
    if (offset >= bytes.length) failCorrupt()
    const marker = bytes[offset]!
    offset += 1
    if (marker === 0xd9 || marker === 0xda) {
      if (width === 0 || height === 0) failCorrupt()
      return { format: 'jpeg', summary, width, height, frameCount: 1 }
    }
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue
    if (offset + 2 > bytes.length) failCorrupt()
    const length = bytes[offset]! * 256 + bytes[offset + 1]!
    if (length < 2 || offset + length > bytes.length) failCorrupt()
    const payloadStart = offset + 2
    if (
      ((marker >= 0xc0 && marker <= 0xc3) ||
        (marker >= 0xc5 && marker <= 0xc7) ||
        (marker >= 0xc9 && marker <= 0xcb) ||
        (marker >= 0xcd && marker <= 0xcf)) &&
      length >= 7
    ) {
      height = bytes[payloadStart + 1]! * 256 + bytes[payloadStart + 2]!
      width = bytes[payloadStart + 3]! * 256 + bytes[payloadStart + 4]!
      validateDimensions(width, height)
    }
    if (
      marker === 0xe1 &&
      length >= 8 &&
      ascii(bytes, payloadStart, payloadStart + 6) === 'Exif\0\0'
    ) {
      summary.exif = true
    }
    if (
      marker === 0xe1 &&
      length >= 31 &&
      ascii(bytes, payloadStart, payloadStart + 29) === 'http://ns.adobe.com/xap/1.0/\0'
    ) {
      summary.xmp = true
    }
    offset += length
  }
  failCorrupt()
}

export function inspectImageMetadata(bytes: Uint8Array): InspectedMetadata {
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    return inspectJpeg(bytes)
  }
  if (bytes.length >= 8 && hasPrefix(bytes, PNG_SIGNATURE)) {
    const chunks = parsePng(bytes)
    const ihdr = chunks[0]!
    const width = readUint32BigEndian(bytes, ihdr.dataStart)
    const height = readUint32BigEndian(bytes, ihdr.dataStart + 4)
    validateDimensions(width, height)
    const frameCount = validateApng(bytes, chunks, width, height)
    return { format: 'png', summary: inspectPng(bytes, chunks), width, height, frameCount }
  }
  if (bytes.length >= 12 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'WEBP') {
    const chunks = parseWebp(bytes)
    const dimensions = readWebpDimensions(bytes, chunks)
    return { format: 'webp', summary: inspectWebp(chunks), ...dimensions }
  }
  throw new MetadataProcessingError('unsupported_type')
}

export function cleanImageMetadata(bytes: Uint8Array): CleanedMetadata {
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    throw new MetadataProcessingError('unsupported_type')
  }
  if (bytes.length >= 8 && hasPrefix(bytes, PNG_SIGNATURE)) {
    const chunks = parsePng(bytes)
    const inspected = inspectImageMetadata(bytes)
    const kept = chunks
      .filter((chunk) => !PNG_REMOVED_CHUNKS.has(chunk.type))
      .map((chunk) => bytes.slice(chunk.start, chunk.end))
    return {
      format: 'png',
      summary: inspected.summary,
      width: inspected.width,
      height: inspected.height,
      frameCount: inspected.frameCount,
      bytes: concat([bytes.slice(0, PNG_SIGNATURE.length), ...kept]),
    }
  }

  if (
    bytes.length < 12 ||
    ascii(bytes, 0, 4) !== 'RIFF' ||
    ascii(bytes, 8, 12) !== 'WEBP'
  ) {
    throw new MetadataProcessingError('unsupported_type')
  }
  const chunks = parseWebp(bytes)
  const dimensions = readWebpDimensions(bytes, chunks)
  const kept = chunks
    .filter((chunk) => !WEBP_REMOVED_CHUNKS.has(chunk.type))
    .map((chunk) => {
      const raw = bytes.slice(chunk.start, chunk.end)
      if (chunk.type === 'VP8X') raw[8] = raw[8]! & ~0x0c
      return raw
    })
  const result = concat([bytes.slice(0, 12), ...kept])
  writeUint32LittleEndian(result, 4, result.length - 8)
  return { format: 'webp', summary: inspectWebp(chunks), ...dimensions, bytes: result }
}
